import { UNIT_MODIFIER_VALUES, relativeRangeToAbsolute } from 'utils/relativeDateTime';
import * as t from './actionTypes';

export const initialState = {
  addEditModalOpen: false,
  campaignFetchParamData: {},
  dashboardData: {},
  defaultStartRange: '-7d',
  defaultEndRange: UNIT_MODIFIER_VALUES.now,
  deleteModalOpen: false,
  endDate: null,
  endTime: null,
  fetchingCampaignsTableIds: [],
  fetchingDashboard: false,
  fetchingData: false,
  layout: [],
  selectedWidget: '',
  settingsModalOpen: false,
  startDate: null,
  startTime: null,
  theme: 'default',
  widgets: {},
};

const dashboardReducer = (state = initialState, action = {}) => {
  switch (action.type) {
    /*--------------------------------------------------------------------------------------------*/
    /* Dashboard Widget Actions (Add)                                                             */
    /*--------------------------------------------------------------------------------------------*/
    case t.ADD_WIDGET_RESPONSE:
      return { ...state, layout: action.layout, widgets: action.widgets };
    case t.ADD_WIDGET_ERROR:
      return { ...state, error: action.error };
    /*--------------------------------------------------------------------------------------------*/
    /* Dashboard Widget Actions (Delete)                                                          */
    /*--------------------------------------------------------------------------------------------*/
    case t.DELETE_WIDGET_RESPONSE:
      return { ...state, layout: action.layout, widgets: action.widgets };
    case t.DELETE_WIDGET_ERROR:
      return { ...state, error: action.error };
    /*--------------------------------------------------------------------------------------------*/
    /* Dashboard Widget Actions (Save)                                                            */
    /*--------------------------------------------------------------------------------------------*/
    case t.SAVE_WIDGET_RESPONSE:
      return { ...state, widgets: action.widgets };
    case t.SAVE_WIDGET_ERROR:
      return { ...state, error: action.error };

    case t.LAYOUT_CHANGE:
      return {
        ...state,
        fetchingDashboard: action.fetchingDashboard,
        layout: action.layout,
        layoutLoading: action.layoutLoading,
      };

    /*--------------------------------------------------------------------------------------------*/
    /* Campaigns Table Widget Actions                                                             */
    /*--------------------------------------------------------------------------------------------*/
    case t.FETCH_CAMPAIGNS_INIT:
      return {
        ...state,
        campaignFetchParamData: {
          ...state.campaignFetchParamData,
          ...action.campaignFetchParamData,
        },
        fetchingCampaignsTableIds: action.fetchingCampaignsTableIds,
      };
    case t.FETCH_CAMPAIGNS_ERROR:
      return {
        ...state,
        fetchingCampaignsTableIds: action.fetchingCampaignsTableIds,
        error: action.error,
      };
    case t.FETCH_CAMPAIGNS_RESPONSE:
      return {
        ...state,
        fetchingCampaignsTableIds: action.fetchingCampaignsTableIds,
        dashboardData: action.dashboardData,
      };

    /*--------------------------------------------------------------------------------------------*/
    /* Add/Edit Modal Actions                                                                     */
    /*--------------------------------------------------------------------------------------------*/
    case t.OPEN_ADD_EDIT_MODAL:
      return {
        ...state,
        addEditModalOpen: action.modalState,
        addMode: action.addMode,
        selectedWidget: action.selectedWidget,
      };
    case t.CLOSE_ADD_EDIT_MODAL:
      return {
        ...state,
        addEditModalOpen: action.modalState,
        addMode: action.addMode,
        selectedWidget: action.selectedWidget,
      };
    case t.CHANGE_SELECTED_WIDGET:
      return { ...state, selectedWidget: action.selectedWidget };

    /*--------------------------------------------------------------------------------------------*/
    /* Delete Modal Actions                                                                       */
    /*--------------------------------------------------------------------------------------------*/
    case t.OPEN_DELETE_MODAL:
      return {
        ...state,
        deleteModalOpen: action.modalState,
        selectedWidget: state.widgets[action.selectedWidgetId],
      };
    case t.CLOSE_DELETE_MODAL:
      return {
        ...state,
        deleteModalOpen: action.modalState,
        selectedWidget: action.selectedWidget,
      };

    /*--------------------------------------------------------------------------------------------*/
    /* Dashboard Settings Modal Actions                                                           */
    /*--------------------------------------------------------------------------------------------*/
    case t.OPEN_SETTINGS_MODAL:
      return {
        ...state,
        settingsModalOpen: action.modalState,
        originalStartRange: state.defaultStartRange,
        originalTheme: state.theme,
      };
    case t.CLOSE_SETTINGS_MODAL:
      return {
        ...state,
        settingsModalOpen: action.modalState,
        originalStartRange: undefined,
        originalTheme: undefined,
        defaultStartRange: action.defaultStartRange || state.originalStartRange,
        theme: action.theme || state.originalTheme,
      };

    /*--------------------------------------------------------------------------------------------*/
    /* Dashboard Settings Actions                                                                 */
    /*--------------------------------------------------------------------------------------------*/
    case t.CHANGE_THEME:
      return { ...state, theme: action.theme };
    case t.CHANGE_DEFAULT_START_RANGE:
      return { ...state, defaultStartRange: action.defaultStartRange };
    case t.CHANGE_DEFAULT_END_RANGE:
      return { ...state, defaultEndRange: action.defaultEndRange };

    /*--------------------------------------------------------------------------------------------*/
    /* Date/Time Range Actions                                                                    */
    /*--------------------------------------------------------------------------------------------*/
    case t.CHANGE_START_DATE:
      return { ...state, startDate: action.startDate };
    case t.CHANGE_END_DATE:
      return { ...state, endDate: action.endDate };
    case t.CHANGE_START_TIME:
      return { ...state, startTime: action.startTime };
    case t.CHANGE_END_TIME:
      return { ...state, endTime: action.endTime };

    /*--------------------------------------------------------------------------------------------*/
    /* Dashboard Fetching Actions                                                                 */
    /*--------------------------------------------------------------------------------------------*/
    case t.FETCH_DASHBOARD:
      return {
        ...state,
        fetchingDashboard: action.fetchingDashboard,
        layoutLoading: action.layoutLoading,
      };
    case t.FETCH_DASHBOARD_ERROR:
      return {
        ...state,
        ...relativeRangeToAbsolute(state.defaultStartRange, state.defaultEndRange),
        fetchingDashboard: action.fetchingDashboard,
        error: action.error,
      };
    case t.FETCH_DASHBOARD_RESPONSE:
      return {
        ...state,
        campaignFetchParamData: action.campaignFetchParamData,
        defaultStartRange: action.defaultStartRange,
        defaultEndRange: action.defaultEndRange,
        fetchingDashboard: action.fetchingDashboard,
        startDate: action.startDate,
        endDate: action.endDate,
        startTime: action.startTime,
        endTime: action.endTime,
        layout: action.layout,
        theme: action.theme,
        widgets: action.widgets,
      };

    /*--------------------------------------------------------------------------------------------*/
    /* Dashboard Fetching Widget Data Actions                                                     */
    /*--------------------------------------------------------------------------------------------*/
    case t.FETCH_DASHBOARD_DATA:
      return { ...state, fetchingData: action.fetchingData };
    case t.FETCH_DASHBOARD_DATA_INIT:
      return { ...state, dashboardData: action.dashboardData };
    case t.FETCH_DASHBOARD_DATA_COMPLETE:
      return { ...state, fetchingData: action.fetchingData };
    case t.FETCH_DASHBOARD_DATA_ERROR:
      return { ...state, error: action.error };
    case t.FETCH_DASHBOARD_DATA_WIDGET_ERROR:
      return { ...state, error: action.error };
    case t.FETCH_DASHBOARD_DATA_RESPONSE:
      return { ...state, dashboardData: action.dashboardData };

    /*--------------------------------------------------------------------------------------------*/
    /* Dashboard Saving Actions                                                                   */
    /*--------------------------------------------------------------------------------------------*/
    case t.SAVE_DASHBOARD_ERROR:
      return { ...state, error: action.error };
    case t.RESET_DASHBOARD:
      return initialState;
    default:
      return state;
  }
};

export default dashboardReducer;
